// Convert a `ToggleState` (the toggle-based UI shape produced by
// `graphToToggleState`) into a compact, human-readable list of
// "which rules were active and with what settings" — the payload
// behind each row's dropdown in the versions drawer.
//
// Only enabled rules are included. Disabled rules are omitted, not
// rendered as "off": the caller's UX is "here is what this version
// actually did", not a re-render of the full editor.
//
// Labels reuse the editor's `*_NAME` AppStrings keys so the drawer
// and the Rules tab stay in sync. Detail templates live under
// `RULES_VERSIONS_SUMMARY_*` and are interpolated at call time via
// `formatString` (so TCM overrides apply).

import { AppStrings } from "../../utils/constants/AppStrings";
import { formatString } from "../../utils/constants/stringUtils";
import {
  TIER_1_RULE_IDS,
  TIER_2_RULE_IDS,
  type PeriodValue,
  type Tier1RuleId,
  type Tier2RuleId,
  type ToggleState,
} from "./mappers";

export interface RuleSummary {
  /**
   * Stable id — the same key used by the mapper and RulesTab, so
   * consumers can wire this to `data-testid` predictably in tests.
   */
  readonly id: Tier1RuleId | Tier2RuleId;
  /**
   * Short display name (matches the label the editor shows above
   * the row's inputs).
   */
  readonly label: string;
  /**
   * One-line human description of the rule's specific settings for
   * this version. Interpolates the ToggleState values via
   * AppStrings templates — no rounding, no validation.
   */
  readonly detail: string;
}

export interface ToggleSummary {
  readonly tier1: readonly RuleSummary[];
  readonly tier2: readonly RuleSummary[];
}

/**
 * Pluralise the period word based on `count`. Non-numeric or blank
 * inputs fall back to the plural — the drawer never shows singular
 * with an unset value, which would read wrong.
 *
 * Period words are read from AppStrings at call time so TCM overrides apply.
 */
function periodWord(unit: PeriodValue, singular: boolean): string {
  switch (unit) {
    case "HOUR":
      return singular
        ? AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_HOUR
        : AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_HOURS;
    case "DAY":
      return singular
        ? AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_DAY
        : AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_DAYS;
    case "WEEK":
      return singular
        ? AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_WEEK
        : AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_WEEKS;
    case "MONTH":
      return singular
        ? AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_MONTH
        : AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_MONTHS;
    case "YEAR":
      return singular
        ? AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_YEAR
        : AppStrings.RULES_VERSIONS_SUMMARY_PERIOD_YEARS;
  }
}

function period(count: string, unit: PeriodValue): string {
  return periodWord(unit, count.trim() === "1");
}

function paymentInstrumentLookback(value: string): string {
  const match = value.match(/^([1-9]\d*)([hdw])$/);
  if (!match) return orUnset(value);
  const unit: PeriodValue =
    match[2] === "h" ? "HOUR" : match[2] === "d" ? "DAY" : "WEEK";
  return formatString(
    AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_FORMAT,
    {
      count: match[1],
      period: period(match[1], unit),
    },
  );
}

/**
 * Placeholder used when a rule is enabled but its value input is
 * empty. Should never appear for a *published* graph — but if the
 * mapper ever loosens its validation, this avoids rendering
 * "Trigger if withdrawal >  $" with an ugly gap.
 */
const UNSET = "?";

function orUnset(value: string | undefined | null): string {
  return value == null || value.trim() === "" ? UNSET : value;
}

export function summarizeToggleState(state: ToggleState): ToggleSummary {
  const tier1: RuleSummary[] = [];
  const tier2: RuleSummary[] = [];

  const maxAmount = state.tier1[TIER_1_RULE_IDS.MAX_AMOUNT];
  if (maxAmount.enabled) {
    tier1.push({
      id: TIER_1_RULE_IDS.MAX_AMOUNT,
      label: AppStrings.TIER_1_RULE_MAX_AMOUNT_NAME,
      detail: formatString(AppStrings.RULES_VERSIONS_SUMMARY_MAX_AMOUNT_DETAIL, {
        amount: `$${orUnset(maxAmount.amount)}`,
      }),
    });
  }

  const firstTime = state.tier1[TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL];
  if (firstTime.enabled) {
    tier1.push({
      id: TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL,
      label: AppStrings.TIER_1_RULE_FIRST_TIME_WITHDRAWAL_NAME,
      detail: AppStrings.RULES_VERSIONS_SUMMARY_FIRST_TIME_WITHDRAWAL_DETAIL,
    });
  }

  const perDay = state.tier1[TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY];
  if (perDay.enabled) {
    tier1.push({
      id: TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY,
      label: AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_NAME,
      detail: formatString(
        AppStrings.RULES_VERSIONS_SUMMARY_WITHDRAWAL_PER_DAY_DETAIL,
        {
          threshold: orUnset(perDay.threshold),
          days: orUnset(perDay.days),
        },
      ),
    });
  }

  const sinceDeposit = state.tier1[TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT];
  if (sinceDeposit.enabled) {
    tier1.push({
      id: TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT,
      label: AppStrings.TIER_1_RULE_WITHDRAWALS_SINCE_DEPOSIT_NAME,
      detail: formatString(
        AppStrings.RULES_VERSIONS_SUMMARY_WITHDRAWALS_SINCE_DEPOSIT_DETAIL,
        { withdrawals: orUnset(sinceDeposit.withdrawals) },
      ),
    });
  }

  const xTimes = state.tier1[TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT];
  if (xTimes.enabled) {
    tier1.push({
      id: TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT,
      label: AppStrings.TIER_1_RULE_X_TIMES_LAST_DEPOSIT_NAME,
      detail: formatString(
        AppStrings.RULES_VERSIONS_SUMMARY_X_TIMES_LAST_DEPOSIT_DETAIL,
        { multiplier: orUnset(xTimes.multiplier) },
      ),
    });
  }

  const reviewed = state.tier2[TIER_2_RULE_IDS.REVIEWED_X_TIMES];
  if (reviewed.enabled) {
    tier2.push({
      id: TIER_2_RULE_IDS.REVIEWED_X_TIMES,
      label: AppStrings.TIER_2_RULE_REVIEWED_X_TIMES_NAME,
      detail: formatString(
        AppStrings.RULES_VERSIONS_SUMMARY_REVIEWED_X_TIMES_DETAIL,
        {
          reviews: orUnset(reviewed.reviews),
          within: orUnset(reviewed.within),
          period: period(reviewed.within, reviewed.period),
        },
      ),
    });
  }

  const vip = state.tier2[TIER_2_RULE_IDS.VIP_MATCHING];
  if (vip.enabled) {
    tier2.push({
      id: TIER_2_RULE_IDS.VIP_MATCHING,
      label: AppStrings.TIER_2_RULE_VIP_MATCHING_NAME,
      detail: formatString(AppStrings.RULES_VERSIONS_SUMMARY_VIP_MATCHING_DETAIL, {
        within: orUnset(vip.within),
        period: period(vip.within, vip.period),
      }),
    });
  }

  const sameInstrument = state.tier2[TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES];
  if (sameInstrument.enabled) {
    tier2.push({
      id: TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES,
      label: AppStrings.TIER_2_RULE_SAME_INSTRUMENT_X_TIMES_NAME,
      detail: formatString(
        AppStrings.RULES_VERSIONS_SUMMARY_SAME_INSTRUMENT_X_TIMES_DETAIL,
        {
          uses: orUnset(sameInstrument.uses),
          within: orUnset(sameInstrument.within),
          period: period(sameInstrument.within, sameInstrument.period),
        },
      ),
    });
  }

  const minAge = state.tier2[TIER_2_RULE_IDS.MIN_ACCOUNT_AGE];
  if (minAge.enabled) {
    tier2.push({
      id: TIER_2_RULE_IDS.MIN_ACCOUNT_AGE,
      label: AppStrings.TIER_2_RULE_MIN_ACCOUNT_AGE_NAME,
      detail: formatString(
        AppStrings.RULES_VERSIONS_SUMMARY_MIN_ACCOUNT_AGE_DETAIL,
        { days: orUnset(minAge.days) },
      ),
    });
  }

  const depositPaymentInstrument =
    state.tier2[TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK];
  if (depositPaymentInstrument.enabled) {
    tier2.push({
      id: TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK,
      label:
        AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_NAME,
      detail: formatString(
        AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_SUMMARY_DETAIL,
        {
          lookback: paymentInstrumentLookback(
            depositPaymentInstrument.lookback,
          ),
        },
      ),
    });
  }

  return { tier1, tier2 };
}
